import { KLineData } from 'klinecharts';
import { klineLineStyle, lineStyleToKLine } from './lineStyle';

interface BollingerBandsIndicator {
  name: string;
  shortName: string;
  calcParams: any[];
  series: 'price' | 'volume' | 'normal';
  figures: Array<{
    key: string;
    title: string;
    type: string;
  }>;
  regenerateFigures?: (params: any[]) => Array<{
    key: string;
    title: string;
    type: string;
  }>;
  calc: (
    dataList: KLineData[],
    indicator: {
      calcParams: any[];
      figures: any[];
    }
  ) => any[];
  styles?: any; // Dodajemy styles
}

// Helper function to get price value based on "Apply to" setting
const getPriceValue = (kLineData: KLineData, applyTo: string): number => {
  switch (applyTo) {
    case 'Open':
      return kLineData.open;
    case 'High':
      return kLineData.high;
    case 'Low':
      return kLineData.low;
    case 'Close':
      return kLineData.close;
    case 'Median Price (HL/2)':
      return (kLineData.high + kLineData.low) / 2;
    case 'Typical Price (HLC/3)':
      return (kLineData.high + kLineData.low + kLineData.close) / 3;
    case 'Weighted Close (HLCC/4)':
      return (kLineData.high + kLineData.low + kLineData.close * 2) / 4;
    default:
      return kLineData.close;
  }
};

export const createBollingerBands = (settings: any): BollingerBandsIndicator => {
  const values = settings?.values ?? settings ?? {};
  const period = values.Period || 20;
  const shift = values.Shift || 0;
  const deviations = values.Deviations || 2.0;
  const applyTo = values['Apply to'] || 'Close';
  const styles = settings?.styles ?? settings?.styleSettings ?? {};
  const legacyColors = settings?.colors ?? {};

  const upperColor =
    styles.upper?.color ?? legacyColors.upper ?? '#FF9600';
  const middleColor =
    styles.middle?.color ?? legacyColors.middle ?? '#2196F3';
  const lowerColor =
    styles.lower?.color ?? legacyColors.lower ?? '#FF9600';
  const upperLineStyle = lineStyleToKLine(styles.upper?.style);
  const middleLineStyle = lineStyleToKLine(styles.middle?.style);
  const lowerLineStyle = lineStyleToKLine(styles.lower?.style);

  return {
    name: 'bollinger-bands', // Changed from 'BOLLINGER_BANDS' to match indicatorId
    shortName: `BB${period}`,
    calcParams: [period, shift, deviations, applyTo],
    series: 'price',
    figures: [
      {
        key: 'upper',
        title: 'Upper: ',
        type: 'line',
      },
      {
        key: 'middle',
        title: 'Middle: ',
        type: 'line',
      },
      {
        key: 'lower',
        title: 'Lower: ',
        type: 'line',
      },
    ],
    styles: {
      lines: [
        klineLineStyle(upperColor, 1, upperLineStyle),
        klineLineStyle(middleColor, 2, middleLineStyle),
        klineLineStyle(lowerColor, 1, lowerLineStyle),
      ],
    },
    regenerateFigures: (params: any[]) => {
      return [
        {
          key: 'upper',
          title: 'Upper: ',
          type: 'line',
        },
        {
          key: 'middle',
          title: 'Middle: ',
          type: 'line',
        },
        {
          key: 'lower',
          title: 'Lower: ',
          type: 'line',
        },
      ];
    },
    /**
     * Single-pass O(N) Bollinger Bands.
     *
     * Uses a sliding window with running sum + running sum of squares.
     * Variance = E[x^2] - E[x]^2 over the last `period` price values.
     * No per-bar slice/reduce or Math.pow allocations.
     */
    calc: (dataList, indicator) => {
      const { calcParams = [period, shift, deviations, applyTo] } = indicator;
      const [currentPeriod, currentShift, currentDeviations, currentApplyTo] =
        calcParams as [number, number, number, string];
      const n = dataList.length;
      const p = currentPeriod;
      const results: Array<{ upper?: number; middle?: number; lower?: number }> =
        new Array(n);

      let sum = 0;
      let sumSquares = 0;

      for (let i = 0; i < n; i++) {
        const priceValue = getPriceValue(dataList[i], currentApplyTo);
        sum += priceValue;
        sumSquares += priceValue * priceValue;

        if (i >= p) {
          const out = getPriceValue(dataList[i - p], currentApplyTo);
          sum -= out;
          sumSquares -= out * out;
        }

        if (i >= p - 1) {
          const mean = sum / p;
          // Clamp tiny negative variance from float noise.
          const variance = Math.max(0, sumSquares / p - mean * mean);
          const stdDev = Math.sqrt(variance);
          const dev = stdDev * currentDeviations;
          results[i] = {
            middle: mean,
            upper: mean + dev,
            lower: mean - dev,
          };
        } else {
          results[i] = {};
        }
      }

      if (currentShift !== 0) {
        const shifted: Array<{ upper?: number; middle?: number; lower?: number }> =
          new Array(n);
        for (let i = 0; i < n; i++) {
          shifted[i] = {};
        }
        for (let i = 0; i < n; i++) {
          const target = i + currentShift;
          if (target >= 0 && target < n) {
            shifted[target] = results[i];
          }
        }
        return shifted;
      }

      return results;
    },
  };
};